/**
 * A per-event map in localStorage, used to remember what a viewer was looking at across reloads.
 *
 * Every function here is total: localStorage can throw (private mode, quota, a partitioned
 * third-party iframe) and its contents can be anything at all — neither is worth breaking the
 * player over, so failures degrade to "nothing remembered".
 */

const MAX_EVENTS = 20;

function readMap(storageKey: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw != null ? JSON.parse(raw) : null;
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return parsed as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** The value stored for this event, or `null` when nothing valid is stored. */
export function readEventMemory<T>(
  storageKey: string,
  eventId: string,
  isValid: (value: unknown) => value is T,
): T | null {
  const value = readMap(storageKey)[eventId];

  return isValid(value) ? value : null;
}

/**
 * Stores the value for this event, forgetting the least recently written event once more than
 * `MAX_EVENTS` events are stored.
 *
 * Other events' entries are carried over verbatim — they are validated when read, so one broken
 * entry cannot take its neighbours down with it.
 */
export function writeEventMemory<T>(storageKey: string, eventId: string, value: T): void {
  try {
    // Re-inserting the event last makes the key order a least-recently-used list. Event ids are
    // never integer-like, so JSON/object key order stays insertion order.
    const others = Object.entries(readMap(storageKey)).filter(([id]) => id !== eventId);
    const kept = [...others, [eventId, value] as [string, unknown]].slice(-MAX_EVENTS);

    localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // ponytail: losing the memory is not worth breaking the player over — the current session
    // still works, it just will not be remembered.
  }
}
